import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../agent-config/agent-config.service';
import { ShareAgentThreadRequest, ShareAgentThreadResponse } from '../../../models';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';

/**
 * Share Agent Thread Service
 *
 * Handles generating a share link for a single conversation thread.
 *
 * Features:
 * - Generate share link by thread identifiers
 * - Mock mode for development/testing
 * - Configurable via JSON files
 * - Error handling and logging
 */
@Injectable({
  providedIn: 'root',
})
export class ShareAgentThreadService {
  private useMock: boolean = true;
  private baseUrl: string = '/api/agent';
  private endpointPath: string = '/share_thread';
  private mockResponse: ShareAgentThreadResponse | null = null;

  constructor(
    private logger: LoggerService,
    private agentConfigService: AgentConfigService,
    private agentApiService: AgentApiService
  ) {
    this.loadConfigurationValues();
  }

  /**
   * Load configuration values from AgentConfigService
   * Note: AgentConfigService is already loaded during app initialization,
   * so we just extract the values we need. URL resolution is now handled by AgentApiService.
   */
  private loadConfigurationValues(): void {
    try {
      const apiConfig = this.agentConfigService.getApiConfig() || {};
      this.useMock = apiConfig?.useMock ?? true;
      this.baseUrl = apiConfig?.baseUrl || '/api/agent';
      this.endpointPath = '/share_thread';

      if (this.useMock) {
        const mockConfig = this.agentConfigService.getMockResponses();
        this.mockResponse = mockConfig?.responses?.share_agent_thread || null;
      }

      const configuredEndpoint =
        this.agentApiService.getApiUrlFromConfig('share_agent_thread');

      this.logger.logInfo(
        `[ShareAgentThread] Configuration loaded - useMock: ${
          this.useMock
        }, endpoint: ${
          configuredEndpoint || `${this.baseUrl}${this.endpointPath}`
        }`,
        'loadConfigurationValues'
      );
    } catch (error: any) {
      this.logger.logInfo(
        '[ShareAgentThread] Error loading configuration values, using defaults',
        'loadConfigurationValues',
        error
      );
    }
  }

  /**
   * Share Agent Thread
   *
   * Generates a share link for a conversation thread.
   *
   * @param request - ShareAgentThreadRequest containing required identifiers
   * @param agentId - Optional agent identifier for agent-specific routing
   * @returns Observable<ShareAgentThreadResponse>
   */
  ShareAgentThread(
    request: ShareAgentThreadRequest,
    agentId?: string
  ): Observable<ShareAgentThreadResponse> {
    try {
      const validationError = this.validateRequest(request);
      if (validationError) {
        return of(validationError);
      }

      // Check whether a share_agent_thread endpoint is configured for this agent.
      // If not, return a client-side placeholder link immediately (no API call).
      // Once the endpoint is added to api_endpoints.json the real API kicks in automatically.
      const configuredEndpoint = this.agentApiService.getApiUrlFromConfig('share_agent_thread', agentId);

      if (!configuredEndpoint) {
        this.logger.logInfo(
          `[ShareAgentThread] No share_agent_thread endpoint configured for agent "${agentId}" – returning placeholder`,
          'ShareAgentThread'
        );
        return of({
          status: 'success',
          message: 'Placeholder share link (no API endpoint configured)',
          data: {
            share_link: '',
            thread_id: request.thread_id,
            tenant_id: request.tenant_id,
            placeholder: true
          }
        } as any);
      }

      if (this.useMock && this.mockResponse) {
        this.logger.logInfo(
          '[ShareAgentThread] Using mock response',
          'ShareAgentThread'
        );
        return of(this.mockResponse);
      }

      this.logger.logInfo(
        '[ShareAgentThread] Making API call',
        'ShareAgentThread'
      );
      return this.makeApiCall(request, agentId);
    } catch (error: any) {
      this.logger.logInfo(
        '[ShareAgentThread] Error in ShareAgentThread',
        'ShareAgentThread',
        error
      );
      return of(
        this.createErrorResponse('Internal error occurred', 'INTERNAL_ERROR')
      );
    }
  }

  /**
   * Perform API call to generate a share link for the agent thread via AgentApiService.
   *
   * @param request - ShareAgentThreadRequest
   * @param agentId - Agent identifier
   * @returns Observable<ShareAgentThreadResponse>
   */
  private makeApiCall(
    request: ShareAgentThreadRequest,
    agentId?: string
  ): Observable<ShareAgentThreadResponse> {
    try {
      const endpoint =
        this.agentApiService.getApiUrlFromConfig('share_agent_thread', agentId) ||
        `${this.baseUrl}${this.endpointPath}`;

      const requestBody = {
        tenant_id: request.tenant_id,
        thread_id: request.thread_id,
      };

      const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint('share_agent_thread', agentId);

      this.logger.logInfo(
        `[ShareAgentThread] ${agentId || 'default'} – calling ${endpoint} via AgentApiService at ${customBaseUrl}`,
        'makeApiCall'
      );

      return this.agentApiService
        .post<any>(endpoint, requestBody, undefined, customBaseUrl)
        .pipe(
          map((response: any) => {
            const result: ShareAgentThreadResponse = {
              status: 'success',
              message: response?.message || 'Share link generated successfully',
              data: {
                share_link: response?.share_link || response?.data?.share_link || '',
                thread_id: request.thread_id,
                tenant_id: request.tenant_id
              }
            };

            this.logger.logInfo(
              `[ShareAgentThread] ${agentId || 'default'} API call successful`,
              'makeApiCall'
            );
            return result;
          }),
          catchError((error: any) => {
            this.logger.logInfo(
              '[ShareAgentThread] API call failed',
              'makeApiCall',
              error
            );
            return of(this.handleApiError(error));
          })
        );
    } catch (error: any) {
      this.logger.logInfo(
        '[ShareAgentThread] Error making API call',
        'makeApiCall',
        error
      );
      return of(
        this.createErrorResponse('Failed to make API call', 'API_ERROR')
      );
    }
  }

  /**
   * Validate request payload
   *
   * @param request - ShareAgentThreadRequest
   * @returns ShareAgentThreadResponse | null (null if valid)
   */
  private validateRequest(
    request: ShareAgentThreadRequest
  ): ShareAgentThreadResponse | null {
    try {
      if (!request.tenant_id || request.tenant_id.trim() === '') {
        return this.createErrorResponse(
          'tenant_id is required',
          'VALIDATION_ERROR',
          { tenant_id: 'Field is required' }
        );
      }

      if (!request.thread_id || request.thread_id.trim() === '') {
        return this.createErrorResponse(
          'thread_id is required',
          'VALIDATION_ERROR',
          { thread_id: 'Field is required' }
        );
      }

      return null; // Validation passed
    } catch (error: any) {
      this.logger.logInfo(
        '[ShareAgentThread] Error validating request',
        'validateRequest',
        error
      );
      return this.createErrorResponse('Validation error', 'VALIDATION_ERROR');
    }
  }

  /**
   * Handle API errors
   *
   * @param error - HTTP error
   * @returns ShareAgentThreadResponse
   */
  private handleApiError(error: any): ShareAgentThreadResponse {
    try {
      if (error.status === 400) {
        return this.createErrorResponse(
          error.error?.message || 'Invalid request parameters',
          'VALIDATION_ERROR',
          error.error?.details
        );
      }

      if (error.status === 401) {
        return this.createErrorResponse(
          'Authentication required',
          'UNAUTHORIZED'
        );
      }

      if (error.status === 403) {
        return this.createErrorResponse(
          'Access denied for tenant',
          'FORBIDDEN'
        );
      }

      if (error.status === 404) {
        return this.createErrorResponse('Endpoint not found', 'NOT_FOUND');
      }

      if (error.status === 500) {
        return this.createErrorResponse(
          'Internal server error',
          'INTERNAL_ERROR'
        );
      }

      return this.createErrorResponse(
        error.error?.message || 'An unexpected error occurred',
        error.error?.code || 'UNKNOWN_ERROR'
      );
    } catch (e: any) {
      this.logger.logInfo(
        '[ShareAgentThread] Error handling API error',
        'handleApiError',
        e
      );
      return this.createErrorResponse(
        'Failed to handle error',
        'ERROR_HANDLING_ERROR'
      );
    }
  }

  /**
   * Create error response
   *
   * @param message - Error message
   * @param code - Error code
   * @param details - Optional error details
   * @returns ShareAgentThreadResponse
   */
  private createErrorResponse(
    message: string,
    code: string,
    details?: Record<string, string>
  ): ShareAgentThreadResponse {
    const response: ShareAgentThreadResponse = {
      status: 'error',
      message,
      code,
    };

    if (details) {
      response.details = details;
    }

    return response;
  }

  /**
   * Check if service is configured to use mock data
   *
   * @returns boolean
   */
  public isUsingMock(): boolean {
    return this.useMock;
  }

  /**
   * Get current API endpoint
   *
   * @returns string
   */
  public getEndpoint(): string {
    return (
      this.agentApiService.getApiUrlFromConfig('share_agent_thread') ||
      `${this.baseUrl}${this.endpointPath}`
    );
  }
}
